import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["error", "inputRef", "placeholder", "styled", "type", "use", "disabled", "autoFocus", "autoFocusPreventScroll", "disableFocusStyles"];
import { forwardRef, useContext, memo, useEffect } from 'react';
import { lazyEval } from '../../utils/lazyEval';
import ShareInput from '../../components/ShareInput/ShareInput';
import styled from 'foundations-theming/utils';
import { FieldsetContext } from '../../context/FieldsetContext';
import { filterProps } from '../../../utils/filterProps';
import { useMapTokenPropsToCSS } from '../../../hooks/useMapTokenPropsToCSS';
import { useMicroDateInputContext } from '../MicroDateInput/MicroDateInputContext';
import { deepMerge } from '../../utils/deepMerge';
import { useMicroTimeInputContext } from '../MicroTimeInput/MicroTimeInputContext';
import { useIsDisplayOptimized } from '../../context/DisplayOptimizedContext';
import { useComputedPlaceholder } from '../../../hooks/usePlaceholder';
import { getFocusStyleObject } from '../../utils/focus/focus.style';
import { useDisableFocusStyles } from '../../../hooks/useDisableFocusStyles';
import { jsx as _jsx } from "react/jsx-runtime";
const TYPES = ['text', 'email', 'number', 'password', 'search', 'url', 'tel'];
export const StyledInput = styled.input.withConfig({
  displayName: "TextInput__StyledInput",
  componentId: "sc-181pjoz-0"
})(({
  $small,
  $styled,
  $error,
  theme,
  $cssStyles,
  $use,
  $styleOverrides,
  $isDisplayOptimized,
  $isMicroDateInput,
  $disableFocusStyles
}) => {
  const $hasMinimalStyling = $isDisplayOptimized || !$styled;
  const readOnlyAndDisabledStyles = {
    backgroundColor: theme.color['fill-field-disabled'],
    borderColor: theme.color['border-core-disabled'],
    boxShadow: 'none'
  };
  return deepMerge(Object.assign({
    backgroundColor: $use === 'on-dark' ? theme.color['fill-surface-default'] : theme.color['fill-field-default'],
    border: `${theme.borderWidth['100']} solid ${theme.color['border-core-default']}`,
    borderRadius: theme.borderRadius['100'],
    color: theme.color['text-core-default'],
    display: 'block',
    fontFamily: $use === 'code' ? theme.typography['code-100'].fontFamily : 'inherit',
    fontSize: $small ? theme.typography['body-100'].fontSize : theme.typography['body-200'].fontSize,
    fontWeight: $use === 'code' ? theme.typography['code-100'].fontWeight : 'inherit',
    height: $isDisplayOptimized ? '30px' : $small ? '32px' : '40px',
    lineHeight: theme.typography['body-200'].lineHeight,
    letterSpacing: theme.typography['body-200'].letterSpacing,
    paddingInline: $isDisplayOptimized ? theme.space['0'] : $isMicroDateInput ? theme.space['200'] : theme.space['400'],
    paddingBlock: $small || $isDisplayOptimized ? theme.space['100'] : theme.space['200'],
    textAlign: 'left',
    transition: 'all 150ms ease-out',
    verticalAlign: 'middle',
    width: '100%'
  }, getFocusStyleObject({
    theme,
    disableFocusStyles: $disableFocusStyles
  }), {
    // Placeholder plus disabled styles
    '&:disabled::placeholder': {
      color: theme.color['text-core-disabled']
    },
    // Placeholder styles
    '&::placeholder': {
      color: theme.color['text-core-subtle']
    },
    '&[readonly]': {
      "&:not([aria-expanded='true'])": Object.assign({}, readOnlyAndDisabledStyles, {
        color: theme.color['text-core-default']
      })
    }
  }, $hasMinimalStyling && {
    backgroundColor: `${theme.color['fill-transparent-default']} !important`,
    borderColor: `${theme.color['fill-transparent-default']} !important`,
    boxShadow: 'none !important'
  }, $isDisplayOptimized && {
    border: 'none',
    fontSize: 'var(--trellis-typography-body-100-font-size)',
    paddingInlineStart: '0'
  }, $error && {
    borderColor: `${theme.color['border-alert-default']} !important`
  }, $error && !$hasMinimalStyling && {
    backgroundColor: theme.color['fill-field-default']
  }, {
    // Disabled styles
    '&:disabled': Object.assign({}, readOnlyAndDisabledStyles, {
      color: theme.color['text-core-disabled'],
      cursor: 'not-allowed'
    })
  }, $cssStyles), $styleOverrides);
});
export const TextInputWithRef = (_ref, ref) => {
  var _microDateInputContex, _microTimeInputContex;
  let {
      error = false,
      inputRef,
      placeholder,
      styled: _styled = true,
      type = 'text',
      use = 'default',
      disabled,
      autoFocus,
      autoFocusPreventScroll = false,
      disableFocusStyles: disableFocusStylesProp
    } = _ref,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded);
  const {
    domProps,
    tokenProps,
    callbackProps,
    restProps
  } = filterProps(rest);
  const cssStyles = useMapTokenPropsToCSS(tokenProps);
  const {
    size: fieldSize,
    disabled: fieldDisabled
  } = useContext(FieldsetContext);
  const microDateInputContext = useMicroDateInputContext();
  const microTimeInputContext = useMicroTimeInputContext();
  const isDisplayOptimized = useIsDisplayOptimized();
  const computedDisabled = disabled || fieldDisabled;
  const computedPlaceholder = useComputedPlaceholder(lazyEval(placeholder));
  const disableFocusStyles = useDisableFocusStyles(disableFocusStylesProp);

  // Handle autoFocus without scrolling
  // We use useEffect to handle autoFocus instead of the native prop to prevent unwanted scrolling
  // when focusing elements. This is particularly important in dropdown contexts with floating-ui where we don't want
  // the page to scroll when opening a select with a searchbox. https://stackoverflow.com/a/74351505
  // TODO: Bring this implementation to parity with UIComponents in a follow-up issue https://git.hubteam.com/HubSpot/foundations-components/issues/6864
  const computedInputRef = inputRef || ref;
  useEffect(() => {
    if (autoFocus && computedInputRef !== null && computedInputRef !== void 0 && computedInputRef.current) {
      computedInputRef.current.focus({
        preventScroll: autoFocusPreventScroll
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return /*#__PURE__*/_jsx(StyledInput, Object.assign({}, domProps, callbackProps, restProps, {
    $cssStyles: cssStyles,
    $error: error,
    $isDisplayOptimized: isDisplayOptimized,
    $isMicroDateInput: !!microDateInputContext,
    $small: fieldSize === 'small',
    $styled: _styled,
    $styleOverrides: deepMerge((_microDateInputContex = microDateInputContext === null || microDateInputContext === void 0 ? void 0 : microDateInputContext.microInputStyleOverrides) !== null && _microDateInputContex !== void 0 ? _microDateInputContex : {}, (_microTimeInputContex = microTimeInputContext === null || microTimeInputContext === void 0 ? void 0 : microTimeInputContext.microInputStyleOverrides) !== null && _microTimeInputContex !== void 0 ? _microTimeInputContex : {}),
    $use: use,
    placeholder: computedPlaceholder,
    ref: computedInputRef,
    type: type,
    onKeyDown: rest.onKeyDown,
    disabled: computedDisabled,
    $disableFocusStyles: disableFocusStyles
    // Explicitly set autoFocus to undefined to prevent native autoFocus behavior
    // from interfering with floating-ui's FloatingFocusManager focus management.
    // This ensures proper focus handling in dropdown contexts without unwanted scrolling.
    ,
    autoFocus: undefined
  }));
};
export const TextInput = ShareInput( /*#__PURE__*/memo( /*#__PURE__*/forwardRef(TextInputWithRef)));